/**
 * Legislation Filters Component
 * Reusable filter component for both LegislationPage and AddBillsModal
 * Supports full and compact variants for different contexts
 */

import React from 'react';
import { Search } from 'lucide-react';
import { hasAdvancedSearchSyntax, getSearchPlaceholder, getSearchTitle } from '../../utils/searchUtils';
import { getDefaultDateRange } from '../../hooks/useBillFilters';
import type { BillFilters, FilterOptions, Committee } from '../../hooks/useBillFilters';
import type { LegislationSearchScope } from '../../utils/legislationUrlState';
import { useMobile } from '../../hooks/useMediaQuery';
import { useSessions, CURRENT_SESSION } from '../../hooks/useSessions';
import { HelpTrigger } from '../../help';
import FilterActions from '../FilterActions';
import MultiSelectDropdown, { type MultiSelectOption } from '../MultiSelectDropdown';

export interface LegislationFiltersProps {
  /** Filter state */
  filters: BillFilters;
  /** Available filter options (subjects, sponsors, committees) */
  filterOptions: FilterOptions;
  /** Current search query */
  searchQuery: string;
  /** Status categories from API */
  statusCategories: any[];
  /** Callback when a filter value changes */
  onFilterChange: <K extends keyof BillFilters>(key: K, value: BillFilters[K]) => void;
  /** Committee dropdown uses stable committee ids; clears legacy `committee` string */
  onCommitteeFilterChange: (committeeIds: string[]) => void;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** DB fields vs indexed bill document text (RAG keyword) */
  searchScope?: LegislationSearchScope;
  onSearchScopeChange?: (scope: LegislationSearchScope) => void;
  /** Hide scope control (e.g. modal bill picker) */
  showSearchScope?: boolean;
  /** Callback to clear all filters */
  onClearFilters: () => void;
  /** Callback to save current filter */
  onSaveFilter?: () => void;
  /** Callback to show advanced search help */
  onShowAdvancedHelp?: () => void;
  /** Function to get filtered committees based on chamber */
  getFilteredCommittees: () => { id: string | number; name: string; chamber: string }[];
  /** Function to format committee display name */
  formatCommitteeDisplayName: (committee: Committee) => string;
  /** Variant: 'full' for page layout, 'compact' for modal layout */
  variant?: 'full' | 'compact';
  /** Whether to show tracking status filter (hide in AddBillsModal) */
  showTrackingStatus?: boolean;
  /** Whether to show save filter button */
  showSaveButton?: boolean;
  /** Save button text (defaults to FilterActions' own). */
  saveButtonLabel?: string;
  /** Save button icon (defaults to FilterActions' own). */
  saveButtonIcon?: import('lucide-react').LucideIcon;
  /** Whether to show advanced search help link */
  showAdvancedSearchHelp?: boolean;
  /** Rendered immediately left of Clear filters (e.g. a saved-view picker). */
  actionsLeading?: React.ReactNode;
  /** Custom className for the container */
  className?: string;
}

const FALLBACK_STATUS_OPTIONS: MultiSelectOption[] = [
  { value: 'signed', label: 'Signed/Became Law' },
  { value: 'passed', label: 'Passed' },
  { value: 'committee', label: 'In Committee' },
  { value: 'introduced', label: 'Introduced' },
  { value: 'postpone_indefinitely', label: 'Postpone Indefinitely' },
];

/** Past this many options a bare checkbox list is harder to scan than the native select it replaced. */
const SEARCHABLE_OPTION_THRESHOLD = 15;

export interface BillFilterFieldsProps {
  filters: BillFilters;
  filterOptions: FilterOptions;
  statusCategories: any[];
  onFilterChange: <K extends keyof BillFilters>(key: K, value: BillFilters[K]) => void;
  onCommitteeFilterChange: (committeeIds: string[]) => void;
  getFilteredCommittees: () => { id: string | number; name: string; chamber: string }[];
  formatCommitteeDisplayName: (committee: Committee) => string;
  labelClass: string;
  /** 'input text-sm' in the compact modal, 'input' on the page. */
  inputClass: string;
}

/**
 * The chamber + multi-select filter fields.
 *
 * One definition rather than one per layout: this block was previously copied
 * between the compact (modal) and full (page) variants, so every filter change
 * had to be made twice and the two drifted. Mobile renders none of this — it
 * returns a search box only, above.
 */
export const BillFilterFields: React.FC<BillFilterFieldsProps> = ({
  filters,
  filterOptions,
  statusCategories,
  onFilterChange,
  onCommitteeFilterChange,
  getFilteredCommittees,
  formatCommitteeDisplayName,
  labelClass,
  inputClass,
}) => {
  const statusOptions: MultiSelectOption[] = statusCategories.length
    ? [...statusCategories]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((c) => ({ value: c.name, label: c.displayName }))
    : FALLBACK_STATUS_OPTIONS;

  const subjectOptions = filterOptions.subjects.map((s) => ({ value: s, label: s }));
  const sponsorOptions = filterOptions.sponsors.map((s) => ({ value: s, label: s }));
  const committeeOptions = getFilteredCommittees().map((c) => ({
    value: String(c.id),
    label: formatCommitteeDisplayName(c),
  }));

  /*
    Session options come from the registry, with real labels — this control is
    THE session scope for the page (the header chip was removed 2026-08-26; two
    controls for one value read as two different filters).

    The stored filter may carry the CURRENT_SESSION sentinel so saved views and
    the page default keep following whichever session is current (PRD §5.D4).
    The dropdown shows the sentinel as the real current session, and selecting
    the current session stores the sentinel back — previously the sentinel
    matched no option and the trigger rendered blank.
  */
  const { sessions, currentSessionKey } = useSessions();
  const sessionSelectOptions = sessions.map((s) => ({
    value: s.sessionKey,
    label: s.isCurrent ? `${s.label} (current)` : s.label,
  }));
  const selectedSessions = filters.session.map((v) =>
    v === CURRENT_SESSION && currentSessionKey ? currentSessionKey : v
  );
  const handleSessionChange = (next: string[]) =>
    onFilterChange(
      'session',
      next.map((v) => (v === currentSessionKey ? CURRENT_SESSION : v))
    );

  return (
    <>
      {/* Chamber stays single-select: the values are mutually exclusive groupings, not tags. */}
      <div>
        <label className={labelClass}>Chamber:</label>
        <select
          aria-label="Chamber filter"
          value={filters.chamber}
          onChange={(e) => onFilterChange('chamber', e.target.value)}
          className={inputClass}
        >
          <option value="All">All Chamber Bills</option>
          <option value="House">House Bills</option>
          <option value="Senate">Senate Bills</option>
          <option value="Resolutions">Resolutions</option>
        </select>
      </div>

      <div>
        {/* The session-scope contextual moved here from the retired chip's menu
            — the trigger sits where someone already asking "which session?" is
            looking. */}
        <label className={`${labelClass} flex items-center gap-1`}>
          Session:
          <HelpTrigger contentKey="session-scope" size="sm" />
        </label>
        <MultiSelectDropdown
          ariaLabel="Session filter"
          options={sessionSelectOptions}
          selected={selectedSessions}
          onChange={handleSessionChange}
          allLabel="All sessions"
          selectedWord="sessions"
        />
      </div>

      <div>
        <label className={labelClass}>Subjects:</label>
        <MultiSelectDropdown
          ariaLabel="Subjects filter"
          options={subjectOptions}
          selected={filters.subject}
          onChange={(next) => onFilterChange('subject', next)}
          allLabel="All subjects"
          selectedWord="subjects"
          searchable={subjectOptions.length > SEARCHABLE_OPTION_THRESHOLD}
          searchPlaceholder="Search subjects…"
        />
      </div>

      <div>
        <label className={labelClass}>Status:</label>
        <MultiSelectDropdown
          ariaLabel="Status filter"
          options={statusOptions}
          selected={filters.status}
          onChange={(next) => onFilterChange('status', next)}
          allLabel="All statuses"
          selectedWord="statuses"
        />
      </div>

      <div>
        <label className={labelClass}>Sponsor:</label>
        <MultiSelectDropdown
          ariaLabel="Sponsor filter"
          options={sponsorOptions}
          selected={filters.sponsor}
          onChange={(next) => onFilterChange('sponsor', next)}
          allLabel="All sponsors"
          selectedWord="sponsors"
          searchable={sponsorOptions.length > SEARCHABLE_OPTION_THRESHOLD}
          searchPlaceholder="Search sponsors…"
        />
      </div>

      <div>
        <label className={labelClass}>Committee:</label>
        <MultiSelectDropdown
          ariaLabel="Committee filter"
          options={committeeOptions}
          selected={filters.committee_id}
          onChange={onCommitteeFilterChange}
          allLabel="All committees"
          selectedWord="committees"
          searchable={committeeOptions.length > SEARCHABLE_OPTION_THRESHOLD}
          searchPlaceholder="Search committees…"
        />
      </div>
    </>
  );
};

/**
 * LegislationFilters component displays all bill filtering options
 * including search, chamber, subject, status, sponsor, committee, dates, and tracking status
 */
const LegislationFilters: React.FC<LegislationFiltersProps> = ({
  filters,
  filterOptions,
  searchQuery,
  statusCategories,
  onFilterChange,
  onCommitteeFilterChange,
  onSearchChange,
  searchScope = 'metadata',
  onSearchScopeChange,
  showSearchScope = true,
  onClearFilters,
  onSaveFilter,
  onShowAdvancedHelp,
  getFilteredCommittees,
  formatCommitteeDisplayName,
  variant = 'full',
  showTrackingStatus = true,
  showSaveButton = true,
  saveButtonLabel,
  saveButtonIcon,
  showAdvancedSearchHelp = true,
  actionsLeading,
  className = ''
}) => {
  const isMobile = useMobile();
  const isCompact = variant === 'compact';
  const ragFulltextDateRangeTitle =
    'In full bill text mode, start/end dates are sent to search (indexed document dates) and used with other filters on bill records.';
  // MOBILE: Show only simple search field
  if (isMobile) {
    return (
      <div className={`${className} space-y-2`}>
        {showSearchScope && onSearchScopeChange && (
          <div>
            <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Search in
            </label>
            <select
              aria-label="Search scope"
              value={searchScope}
              onChange={(e) =>
                onSearchScopeChange(e.target.value as LegislationSearchScope)
              }
              className="input w-full text-sm"
            >
              <option value="metadata">Summary & metadata</option>
              <option value="fulltext">Full bill text</option>
            </select>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
          <input
            type="text"
            aria-label="Bill search"
            placeholder={getSearchPlaceholder(searchQuery, searchScope)}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        {showAdvancedSearchHelp && onShowAdvancedHelp && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onShowAdvancedHelp}
              className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
            >
              <Search className="h-3 w-3" />
              Advanced Syntax
            </button>
          </div>
        )}
      </div>
    );
  }

  // Spacing classes based on variant
  const labelClass = isCompact 
    ? 'block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1'
    : 'block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2';
  
  const gridClass = isCompact
    ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2'
    : 'grid grid-cols-1 md:grid-cols-3 gap-4';

  const handleDateCheckboxChange = (type: 'introduced' | 'amended', checked: boolean) => {
    const newFilters: Partial<BillFilters> = {
      [type === 'introduced' ? 'includeIntroduced' : 'includeAmended']: checked
    };
    
    // Auto-fill date range if checkbox is checked and dates are empty
    if ((checked || (type === 'introduced' ? filters.includeAmended : filters.includeIntroduced)) && !filters.startDate) {
      const defaultRange = getDefaultDateRange();
      newFilters.startDate = defaultRange.startDate;
      newFilters.endDate = defaultRange.endDate;
    }
    
    // Apply all changes at once
    Object.entries(newFilters).forEach(([key, value]) => {
      onFilterChange(key as keyof BillFilters, value);
    });
  };

  // Compact container style (no card wrapper, used in modal)
  if (isCompact) {
    return (
      <div className={`space-y-3 ${className}`}>
        {/* Search Field - Full Width */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className={labelClass}>Search:</label>
            {showAdvancedSearchHelp && onShowAdvancedHelp && (
              <button
                type="button"
                onClick={onShowAdvancedHelp}
                className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
              >
                <Search className="h-3 w-3" />
                Advanced
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
              <input
                type="text"
                aria-label="Bill search"
                placeholder={getSearchPlaceholder(searchQuery, searchScope)}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className={`input pl-9 text-sm ${
                  searchScope === 'metadata' && hasAdvancedSearchSyntax(searchQuery)
                    ? 'border-primary-500 ring-1 ring-primary-500'
                    : ''
                }`}
                title={getSearchTitle(searchQuery, searchScope)}
              />
            </div>
            {showSearchScope && onSearchScopeChange && (
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={searchScope === 'fulltext'}
                  onChange={(e) =>
                    onSearchScopeChange(e.target.checked ? 'fulltext' : 'metadata')
                  }
                  className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                />
                <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                  Search full bill text
                </span>
              </label>
            )}
          </div>
        </div>

        {/* Filter Grid - Compact */}
        <div className={gridClass}>
          <BillFilterFields
            filters={filters}
            filterOptions={filterOptions}
            statusCategories={statusCategories}
            onFilterChange={onFilterChange}
            onCommitteeFilterChange={onCommitteeFilterChange}
            getFilteredCommittees={getFilteredCommittees}
            formatCommitteeDisplayName={formatCommitteeDisplayName}
            labelClass={labelClass}
            inputClass="input text-sm"
          />

        {/* Date Filters - Compact */}
        <div>
          <label
            className={labelClass}
          >
            Date Filters:
          </label>
          <div className="flex items-center gap-4">
            <label
              className="flex items-center space-x-1.5 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={filters.includeIntroduced}
                onChange={(e) => handleDateCheckboxChange('introduced', e.target.checked)}
                className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500 w-3.5 h-3.5"
              />
              <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Introduced</span>
            </label>
            <label
              className="flex items-center space-x-1.5 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={filters.includeAmended}
                onChange={(e) => handleDateCheckboxChange('amended', e.target.checked)}
                className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500 w-3.5 h-3.5"
              />
              <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Amended</span>
            </label>
          </div>
        </div>

        {/* Date Range - Compact (date_from / date_to for full bill text) */}
        <div className="min-w-0">
          <label
            className={labelClass}
            title={searchScope === 'fulltext' ? ragFulltextDateRangeTitle : undefined}
          >
            Date Range:
          </label>
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => onFilterChange('startDate', e.target.value)}
              className="input text-sm flex-1 min-w-0"
            />
            <span className="text-xs text-text-secondary dark:text-text-dark-secondary shrink-0">to</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => onFilterChange('endDate', e.target.value)}
              className="input text-sm flex-1 min-w-0"
            />
          </div>
        </div>
        </div>
        
        {/* Row 3: Tracking Status (left) + Action Buttons (right) */}
        <div className="flex items-end justify-between flex-wrap gap-3 mt-3">
          {/* Tracking Status - Left */}
          {showTrackingStatus && (
            <div>
              <label className={labelClass}>Tracking Status:</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center space-x-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.trackingStatus === 'tracking'}
                    onChange={(e) => onFilterChange('trackingStatus', e.target.checked ? 'tracking' : '')}
                    className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500 cursor-pointer w-3.5 h-3.5"
                  />
                  <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Currently Tracking</span>
                </label>
                
                <label className="flex items-center space-x-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.trackingStatus === 'not_tracking'}
                    onChange={(e) => onFilterChange('trackingStatus', e.target.checked ? 'not_tracking' : '')}
                    className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500 cursor-pointer w-3.5 h-3.5"
                  />
                  <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Not Tracking</span>
                </label>

                <label className="flex items-center space-x-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.hot}
                    onChange={(e) => onFilterChange('hot', e.target.checked)}
                    className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500 cursor-pointer w-3.5 h-3.5"
                  />
                  <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Hot Bills</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Action Buttons - Right */}
          {actionsLeading && <div className="ml-auto">{actionsLeading}</div>}
          <FilterActions
            className={actionsLeading ? '' : 'ml-auto'}
            onClear={onClearFilters}
            onSave={showSaveButton && onSaveFilter ? onSaveFilter : undefined}
            saveLabel={saveButtonLabel}
            saveIcon={saveButtonIcon}
          />
        </div>
      </div>
    );
  }

  // Full variant - original card layout for page
  return (
    <div className={`card lg:col-span-2 ${className}`}>
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Filters</h3>
      </div>
      
      <div className={gridClass}>
        <BillFilterFields
          filters={filters}
          filterOptions={filterOptions}
          statusCategories={statusCategories}
          onFilterChange={onFilterChange}
          onCommitteeFilterChange={onCommitteeFilterChange}
          getFilteredCommittees={getFilteredCommittees}
          formatCommitteeDisplayName={formatCommitteeDisplayName}
          labelClass={labelClass}
          inputClass="input"
        />
      </div>

      <div className={gridClass}>
        {/* Search Filter - Row 3 Col 1 */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Search:</label>
            {showAdvancedSearchHelp && onShowAdvancedHelp && (
              <button
                type="button"
                onClick={onShowAdvancedHelp}
                className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
              >
                <Search className="h-3 w-3" />
                Advanced Syntax
              </button>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
            <input
              type="text"
              aria-label="Bill search"
              placeholder={getSearchPlaceholder(searchQuery, searchScope)}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={`input pl-10 ${
                searchScope === 'metadata' && hasAdvancedSearchSyntax(searchQuery)
                  ? 'border-primary-500 ring-1 ring-primary-500'
                  : ''
              }`}
              title={getSearchTitle(searchQuery, searchScope)}
            />
          </div>
          {searchScope === 'metadata' && hasAdvancedSearchSyntax(searchQuery) && (
            <div className="mt-1 text-xs text-primary-600 dark:text-primary-400 flex items-center gap-1">
              <Search className="h-3 w-3" />
              Advanced syntax active
            </div>
          )}
          {showSearchScope && onSearchScopeChange && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={searchScope === 'fulltext'}
                onChange={(e) =>
                  onSearchScopeChange(e.target.checked ? 'fulltext' : 'metadata')
                }
                className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Search full bill text
              </span>
            </label>
          )}
        </div>
        
        {/* Date Filters */}
        <div>
          <label
            className={labelClass}
          >
            Date Filters:
          </label>
          <div className="flex items-center gap-4">
            <label
              className="flex items-center space-x-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={filters.includeIntroduced}
                onChange={(e) => handleDateCheckboxChange('introduced', e.target.checked)}
                className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-text-secondary dark:text-text-dark-secondary">Introduced</span>
            </label>
            
            <label
              className="flex items-center space-x-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={filters.includeAmended}
                onChange={(e) => handleDateCheckboxChange('amended', e.target.checked)}
                className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-text-secondary dark:text-text-dark-secondary">Amended</span>
            </label>
          </div>
        </div>

        {/* Date Range (date_from / date_to for full bill text) */}
        <div className="min-w-0">
          <label
            className={labelClass}
            title={searchScope === 'fulltext' ? ragFulltextDateRangeTitle : undefined}
          >
            Date Range:
          </label>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => onFilterChange('startDate', e.target.value)}
              className="input flex-1 min-w-0"
            />
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary shrink-0">to</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => onFilterChange('endDate', e.target.value)}
              className="input flex-1 min-w-0"
            />
          </div>
        </div>
      </div>
      
      {/* Row 4: Tracking Status (left) + Action Buttons (right) */}
      <div className="flex items-end justify-between flex-wrap gap-4">
          {/* Tracking Status - Left */}
          {showTrackingStatus && (
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Tracking Status:</span>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.trackingStatus === 'tracking'}
                  onChange={(e) => onFilterChange('trackingStatus', e.target.checked ? 'tracking' : '')}
                  className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">Currently Tracking</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.trackingStatus === 'not_tracking'}
                  onChange={(e) => onFilterChange('trackingStatus', e.target.checked ? 'not_tracking' : '')}
                  className="rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">Not Tracking</span>
              </label>
            </div>
          )}
          
          {/* Action Buttons - Right */}
          {actionsLeading && <div className="ml-auto">{actionsLeading}</div>}
          <FilterActions
            className={actionsLeading ? '' : 'ml-auto'}
            onClear={onClearFilters}
            onSave={showSaveButton && onSaveFilter ? onSaveFilter : undefined}
            saveLabel={saveButtonLabel}
            saveIcon={saveButtonIcon}
          />
        </div>
      </div>
  );
};

export default LegislationFilters;
